import { useState, useEffect, useMemo } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import apis from "../../context/api";
import { getIsAdmin, MODULES, OPERATIONS } from "../../context/permissions";

// Build an editable matrix { [module]: { canCreate, canView, canUpdate, canDelete } }
// from the modules list and a server permissions array (defaults everything false).
const buildMatrix = (modules, serverPermissions) => {
  const byModule = {};
  (serverPermissions || []).forEach((p) => {
    byModule[p.module] = p;
  });
  const matrix = {};
  modules.forEach((m) => {
    const row = byModule[m] || {};
    matrix[m] = {
      canCreate: !!row.canCreate,
      canView: !!row.canView,
      canUpdate: !!row.canUpdate,
      canDelete: !!row.canDelete,
    };
  });
  return matrix;
};

function Permissions() {
  const isAdmin = getIsAdmin();

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modules, setModules] = useState(MODULES);
  const [operations, setOperations] = useState(OPERATIONS);
  const [matrix, setMatrix] = useState({});
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  // Load users + module/operation config once.
  useEffect(() => {
    if (!isAdmin) return;
    apis
      .getUsers()
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Could not load users:", err));

    apis
      .getPermissionModules()
      .then((data) => {
        if (data?.modules?.length) setModules(data.modules);
        if (data?.operations?.length) setOperations(data.operations);
      })
      .catch((err) => console.error("Could not load module config:", err));
  }, [isAdmin]);

  // Load the selected user's permissions into the editable matrix.
  useEffect(() => {
    if (!selectedUser) {
      setMatrix({});
      return;
    }
    setLoadingMatrix(true);
    apis
      .getUserPermissions(selectedUser.id)
      .then((data) => setMatrix(buildMatrix(modules, data?.permissions)))
      .catch((err) => console.error("Could not load permissions:", err))
      .finally(() => setLoadingMatrix(false));
  }, [selectedUser, modules]);

  const toggle = (module, opKey) => {
    setMatrix((prev) => ({
      ...prev,
      [module]: { ...prev[module], [opKey]: !prev[module]?.[opKey] },
    }));
  };

  const handleSave = () => {
    if (!selectedUser) return;
    setSaving(true);
    // Send only create/view/update; the server forces canDelete = 0.
    const permissions = modules.map((m) => ({
      module: m,
      canCreate: !!matrix[m]?.canCreate,
      canView: !!matrix[m]?.canView,
      canUpdate: !!matrix[m]?.canUpdate,
    }));
    apis
      .updateUserPermissions(selectedUser.id, permissions)
      .then(() => setSuccessOpen(true))
      .catch((err) => console.error("Could not save permissions:", err))
      .finally(() => setSaving(false));
  };

  const userLabel = (u) => `${u.name || "Unnamed"}${u.email ? ` (${u.email})` : ""}`;

  const noAccess = useMemo(
    () => (
      <MDBox p={4} textAlign="center">
        <MDTypography variant="h6" color="text">
          Admin access required.
        </MDTypography>
      </MDBox>
    ),
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={6}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Manage Permissions
                </MDTypography>
              </MDBox>

              {!isAdmin ? (
                noAccess
              ) : (
                <MDBox p={3}>
                  <MDBox mb={3} maxWidth="24rem">
                    <Autocomplete
                      options={users}
                      value={selectedUser}
                      onChange={(e, value) => setSelectedUser(value)}
                      getOptionLabel={(u) => (u ? userLabel(u) : "")}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField {...params} label="Select a user" variant="standard" />
                      )}
                    />
                  </MDBox>

                  {selectedUser && !loadingMatrix && (
                    <>
                      <Table>
                        <TableHead sx={{ display: "table-header-group" }}>
                          <TableRow>
                            <TableCell>
                              <MDTypography variant="caption" fontWeight="bold">
                                Module
                              </MDTypography>
                            </TableCell>
                            {operations.map((op) => (
                              <TableCell key={op.key} align="center">
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  color={op.enabled ? "dark" : "text"}
                                >
                                  {op.label}
                                  {!op.enabled ? " (disabled)" : ""}
                                </MDTypography>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modules.map((m) => (
                            <TableRow key={m}>
                              <TableCell>
                                <MDTypography variant="button" fontWeight="medium">
                                  {m}
                                </MDTypography>
                              </TableCell>
                              {operations.map((op) => (
                                <TableCell key={op.key} align="center">
                                  <Checkbox
                                    checked={!!matrix[m]?.[op.key]}
                                    disabled={!op.enabled}
                                    onChange={() => op.enabled && toggle(m, op.key)}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      <MDBox mt={3} display="flex" justifyContent="flex-end">
                        <MDButton
                          color="info"
                          variant="gradient"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save Permissions"}
                        </MDButton>
                      </MDBox>
                    </>
                  )}

                  {selectedUser && loadingMatrix && (
                    <MDTypography variant="button" color="text">
                      Loading permissions...
                    </MDTypography>
                  )}
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="Permissions updated"
        content="The user's permissions have been saved."
        dateTime=""
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        close={() => setSuccessOpen(false)}
        bgWhite
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Permissions;
